import React from 'react'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import shallow from 'zustand/shallow'
import {
  FormControlLabel,
  withStyles,
  FormLabel,
  FormControl,
  Box,
  Typography,
  TextField,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
  ToggleButtonGroup,
  ToggleButton,
} from '@material-ui/lab'
import SelectBox from '../App/components/SelectBox'
import {
  DEFAULT_ACTIVE_SHAPE,
  GEO_TYPES,
  getStateBounds,
  OPTIONS_METRIC,
  OPTIONS_NORM,
  RATING_TYPES,
  STATE_OPTIONS,
  STATES_WITHOUT_COUNTY_LEVELS,
} from '../../../../constants/map'
import useStore from '../store'
import { PanelBody, PanelHeader } from '../SlideoutPanel'
import { SwitchControl } from '../Controls'
import Tooltip from '../Tooltip'
import { STATES } from '@hyperobjekt/us-states'
import { bbox, polygon } from 'turf'
import { getStateUpdateForPOI, isWkkfView } from '../utils'
const poi_data = require('../../../../constants/poi_data.json')

// Styles for this component.
const styles = theme => ({
  root: {
    '& .panel-body': {
      '& > *': {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
      },
    },
  },
  controlSection: {
    paddingTop: theme.spacing(2),
    borderTop: theme.extras.styles.faintBorder,
    paddingBottom: theme.spacing(2),
    // marginTop: theme.spacing(2),
    position: 'relative',
    '&.main': {
      borderTop: 'none',
    },
  },
  control: {
    // padding: theme.spacing(1, 3),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '& .MuiToggleButtonGroup-root': {
      '& .MuiToggleButton-root': {
        // so it can trigger tooltip
        pointerEvents: 'all',
      },
      '& .Mui-selected': {
        backgroundColor:
          theme.extras.variables.colors.accent,
        border: `1px solid ${theme.extras.variables.colors.accentBackground}`,
        '& p': {
          color: theme.extras.variables.colors.ddkMainBlue,
        },
      },
      '& .Mui-disabled p': {
        color: theme.extras.variables.colors.textSubtle,
      },
    },

    '&.geo-type .MuiToggleButton-root:first-of-type': {
      // prevent "census tracts" from spanning two lines
      flex: '1.5 1 0',
    },
  },
  switchControl: {
    marginLeft: 12,
  },
  controlLabel: {
    ...theme.typography.body2,
    display: 'block',
    position: 'relative',
    width: '100%',
    paddingBottom: theme.spacing(0.5),
    color: theme.extras.variables.colors.textSubtle,
    '&.Mui-focused': {
      color: theme.extras.variables.colors.textSubtle,
    },
  },
  toggleButtonGroup: {
    width: `calc(100% - ${theme.spacing(4)}px)`,
    height: 36,
    // '& .MuiToggleButton-label': {
    //   color: theme.extras.variables.colors.middleGray,
    // },
    '&.fullWidth': {
      paddingRight: theme.spacing(2),
      '& .MuiToggleButton-root': {
        flex: '1 1 0',
      },
    },
  },

  tooltipSwitchControl: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  regionSelected: {},
  activeRegionSelect: {
    '&$regionSelected': {
      '& input': {
        // display: 'none',
        // NOTE: hack to keep the input around invisibly so that clicking triggers dropdown
        opacity: 0,
        width: 'calc(100% - 80px)',
        position: 'absolute',
      },
      '& .MuiChip-root': {
        maxWidth: '100%',
        background: 'none',
      },
      '& .MuiChip-deleteIcon': {
        display: 'none',
      },
    },
  },
})

const createOptions = (prefix, options) => {
  return options.map(el => {
    return {
      val: el,
      display: i18n.translate(
        `${prefix}${el.toUpperCase()}`,
      ),
    }
  })
}

const ToggleGroup = withStyles(styles)(
  ({
    prefix,
    options,
    selected,
    handleChange,
    label,
    fullWidth = false,
    disableOptionCallback = () => null,
    classes,
  }) => {
    const identifier =
      'toggle-' + label.split(' ').join('-')
    return (
      <>
        <FormLabel
          className={classes.controlLabel}
          htmlFor={identifier}
        >
          {label}
        </FormLabel>
        <ToggleButtonGroup
          id={identifier}
          color="primary"
          value={selected}
          exclusive
          onChange={handleChange}
          className={clsx(classes.toggleButtonGroup, {
            fullWidth,
          })}
        >
          {options.map(op => {
            const disabledTooltipText = disableOptionCallback(
              op,
            )
            const disabled =
              typeof disabledTooltipText === 'string'
            let LabelComponent = (
              <Typography variant="body1">
                {i18n.translate(
                  `${prefix}${op.toUpperCase()}`,
                )}
              </Typography>
            )
            if (disabled && disabledTooltipText) {
              LabelComponent = (
                <Tooltip title={disabledTooltipText}>
                  {LabelComponent}
                </Tooltip>
              )
            }
            return (
              <ToggleButton
                value={op}
                key={op}
                disabled={disabled}
              >
                {LabelComponent}
              </ToggleButton>
            )
          })}
        </ToggleButtonGroup>
      </>
    )
  },
)

const DataControls = ({ classes, className, ...props }) => {
  const {
    loadYears,
    activeYear,
    activeNorm,
    activeView,
    activeShape,
    activeMetric,
    activeRegion,
    activePOI,
    POIModeActive,
    setStoreValues,
    setActivePOI,
    displayPopup,
    activeRatingType,
    activeGeoType,
    // slideoutFeature,
    remoteJson,
    flyToBounds,
    flyToFeature,
  } = useStore(
    state => ({
      loadYears: state.loadYears,
      activeYear: state.activeYear,
      activeNorm: state.activeNorm,
      activeView: state.activeView,
      activeShape: state.activeShape,
      activeMetric: state.activeMetric,
      activeRegion: state.activeRegion,
      activePOI: state.activePOI,
      POIModeActive: state.POIModeActive,
      setStoreValues: state.setStoreValues,
      setActivePOI: state.setActivePOI,
      displayPopup: state.displayPopup,
      activeRatingType: state.activeRatingType,
      activeGeoType: state.activeGeoType,
      // slideoutFeature: state.slideoutFeature,
      remoteJson: state.remoteJson,
      flyToBounds: state.flyToBounds,
      flyToFeature: state.flyToFeature,
    }),
    shallow,
  )

  /** Handle active metric changes */
  const handleActiveMetric = event => {
    setStoreValues({
      activeMetric: event.target.value,
    })
  }

  /** Handle active region changes */
  const handleActiveRegion = (a, b, c, selected) => {
    const selectedId = selected?.option?.value || null
    setStoreValues({ activeRegion: selectedId })

    // FLY TO LOCATION
    // first try flying to feature (only works if it's rendered)
    if (activeNorm === 'm') {
      try {
        const normType = {
          s: 'states',
          m: 'metros',
        }[activeNorm]

        const features = window.localMapRef.queryRenderedFeatures(
          {
            layers: [`${normType}Shapes`],
            filter: ['==', ['id'], selectedId],
          },
        )

        if (features.length === 1) {
          flyToFeature(features[0])
          return
        } else if (features.length > 1) {
          // feature is split across tiles: merge before flying
          const allCoords = features.reduce((acc, f) => {
            acc.push(...f.geometry.coordinates)
            return acc
          }, [])
          const poly = polygon(allCoords)
          const box = bbox(poly)
          flyToBounds([
            [box[0], box[1]],
            [box[2], box[3]],
          ])
          return
        }
      } catch (error) {
        // carry on with below method...
      }
    }

    // otherwise fly to bounds of containing state(s)
    let stateCodes = []
    if (activeNorm === 's') {
      // TODO: work around object keys turning from "2" to "02"
      stateCodes = [
        STATES[selectedId]?.abbr ||
          STATES[String(selectedId).padStart(2, 0)]?.abbr,
      ]
    } else if (activeNorm === 'm') {
      const selectedName = selected?.option?.display || ''
      stateCodes = selectedName
        .replace(/^.*, /, '')
        .split('-')
    }

    if (!!stateCodes[0]) {
      const bounds = getStateBounds(stateCodes)
      flyToBounds(bounds)
    }
  }

  /** Handle active POI changes */
  const handleActivePOI = (a, b, c, selected) => {
    const selectedId = selected.option.value
    const activePOIData = poi_data.sites.find(
      s => s.id === selectedId,
    )

    setActivePOI(selectedId)
    setStoreValues(
      getStateUpdateForPOI(selectedId, activeNorm),
    )
    flyToBounds(activePOIData.bbox)

    if (
      !!activeShape &&
      !activePOIData.tracts.includes(activeShape)
    ) {
      // trigger BaseMap's clearFeature as activeShape will no longer be visible
      window.clearActiveMapFeature()
    }
  }

  /** Handle active norm changes */
  const handleActiveNorm = (e, selectedValue) => {
    if (!selectedValue) return

    const values = {
      activeNorm: selectedValue,
      activeRegion: null,
    }
    if (!POIModeActive) {
      setStoreValues(values)
      return
    }

    const activePOIData = poi_data.sites.find(
      s => s.id === activePOI,
    )
    if (selectedValue === 's') {
      values.activeRegion = activePOIData.state_fips
    } else if (selectedValue === 'm') {
      // metro mode toggle will be disabled if POI doesn't belong to a metro
      // so if not disabled, we can assume the POI belongs to a metro
      values.activeRegion = activePOIData.metro_fips
    }

    setStoreValues(values)
  }

  /** Handle active year changes */
  const handleActiveYear = (e, selectedValue) => {
    if (!selectedValue) return
    setStoreValues({
      activeYear: selectedValue,
    })
  }

  /** Handle active year changes */
  const handleActiveRatingType = (e, selectedValue) => {
    if (!selectedValue) return
    setStoreValues({
      activeRatingType: selectedValue,
    })
  }

  /** Handle active data source level changes */
  const handleActiveGeoType = (e, selectedValue) => {
    if (!selectedValue) return
    const newValues = {
      activeGeoType: selectedValue,
      activeShape: DEFAULT_ACTIVE_SHAPE,
      hoveredFeatureId: 0,
      hoveredFeature: null,
      slideoutFeatureId: 0,
      slideoutFeature: null,
    }

    // handle auto-switching (w/notification) in case of invalid mode combinations
    if (selectedValue === 'counties') {
      // COUNTIES
      if (activeNorm === 'm') {
        // COUNTIES & METRO (invalid)
        newValues.activeNorm = 'n'
        newValues.notification = [
          'CONTROL_NORM_COUNTY_METRO_TOOLTIP',
          'NOTIFICATION_NORM_RESET',
        ]
      } else if (activeNorm === 's') {
        // COUNTIES & STATE
        if (activeRatingType === 's') {
          // COUNTIES & STATE & SCORES (invalid)
          newValues.activeRatingType = 'x'
          newValues.notification = [
            'CONTROL_NORM_COUNTY_STATE_TOOLTIP',
            'NOTIFICATION_RATING_RESET',
          ]
        }
        if (
          STATES_WITHOUT_COUNTY_LEVELS.includes(
            activeRegion,
          )
        ) {
          // COUNTIES & STATE & INVALID REGION
          newValues.activeRegion = null
          // NOTE: omit
          // newValues.notification = newValues.notification || []
          // newValues.notification.push()
        }
      }
    } else if (selectedValue === 'zips') {
      // ZIPS
      if (activeNorm === 'm' && activeRatingType === 's') {
        // ZIPS & METRO & SCORES (invalid)
        newValues.activeRatingType = 'x'
        newValues.notification = [
          'CONTROL_NORM_ZIP_METRO_TOOLTIP',
          'NOTIFICATION_RATING_RESET',
        ]
      }
    }

    // HACK: trigger BaseMap's clearFeature before geoType actually changes
    window.clearActiveMapFeature()
    setStoreValues(newValues)
  }

  const toggleShowPopup = () =>
    setStoreValues({ displayPopup: !displayPopup })

  const togglePOIMode = (e, mode) => {
    // mode is null when clicking on selected button
    if (!mode) return
    let values = {}

    // entering POI
    if (!POIModeActive) {
      const activePOIData = poi_data.sites.find(
        s => s.id === activePOI,
      )
      values = getStateUpdateForPOI(activePOI, activeNorm)
      if (activeGeoType !== 'tracts') {
        // only census tracts available in POI mode
        values.activeGeoType = 'tracts'
        values.notification = [
          'WKKF_GEO_TOOLTIP',
          'NOTIFICATION_GEO_RESET',
        ]
      }
      // keep hash updated with active poi
      values.activeView = 'wkkf' + activePOI
      flyToBounds(activePOIData.bbox)

      if (
        !!activeShape &&
        !activePOIData.tracts.includes(activeShape)
      ) {
        // trigger BaseMap's clearFeature as activeShape will no longer be visible
        window.clearActiveMapFeature()
      }
    } else {
      // clear active poi from view param in hash
      values.activeView = 'wkkf'
    }

    setStoreValues({
      ...values,
      POIModeActive: !POIModeActive,
    })
  }

  const createRegionOptions = () => {
    const options = []

    if (activeNorm === 's') {
      options.push(...STATE_OPTIONS)
    } else if (activeNorm === 'm') {
      const metroOptions =
        remoteJson?.metros?.data
          .filter(d => Number(d.i) === 1) // keep only top 100
          .map(d => ({
            display: d.name,
            value: Number(d.GEOID),
          })) || []

      options.push()
      options.push(...metroOptions)
    }

    return options
  }

  const getActiveRegionSelect = () => {
    if (activeNorm === 'n') return null

    const options = createRegionOptions()
    const activeRegionName =
      activeRegion &&
      options.find(o => o.value === activeRegion)?.display

    return (
      <Box className={classes.control}>
        <FormLabel
          className={classes.controlLabel}
          htmlFor={'activeRegion-autocomplete'}
        >
          {i18n.translate('CONTROL_ACTIVE_REGION_TITLE')}
          {activeGeoType === 'counties' && (
            <Tooltip
              title={i18n.translate(
                'CONTROL_ACTIVE_REGION_COUNTY_TOOLTIP',
              )}
            />
          )}
        </FormLabel>
        <Autocomplete
          className={clsx(classes.activeRegionSelect, {
            [classes.regionSelected]: !!activeRegion,
          })}
          // expects arr bc multiple mode
          value={!!activeRegion ? [activeRegion] : []}
          multiple
          // in POI mode, active region is set by active POI
          disabled={!!POIModeActive}
          getOptionDisabled={o =>
            activeGeoType === 'counties' &&
            STATES_WITHOUT_COUNTY_LEVELS.includes(o.value)
          }
          // filterSelectedOptions
          blurOnSelect
          openOnFocus
          id="activeRegion-autocomplete"
          options={options}
          onChange={handleActiveRegion}
          // handles both the dropdown options and the input text
          // necessary to maintain search matching functionality
          getOptionLabel={o =>
            typeof o === 'object'
              ? o.display
              : activeRegionName
          }
          getOptionSelected={(o, value) =>
            o.value === value
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label=""
              onKeyDown={e =>
                // NOTE: hack prevent typing in invisible input
                activeRegion && e.preventDefault()
              }
              InputLabelProps={{ display: 'none' }}
              placeholder={i18n.translate(
                `CONTROL_ACTIVE_REGION_DEFAULT_${activeNorm.toUpperCase()}`,
              )}
            />
          )}
        />
      </Box>
    )
  }

  const getActivePOISelect = () => {
    const options = poi_data.sites.map(({ name, id }) => ({
      display: name,
      value: id,
    }))

    const activePOIName =
      activePOI &&
      options.find(o => o.value === activePOI)?.display

    return (
      <Box className={classes.control}>
        <FormLabel
          className={classes.controlLabel}
          htmlFor={'activePOI-autocomplete'}
        >
          {i18n.translate('WKKF_DROPDOWN_LABEL')}
        </FormLabel>
        <Autocomplete
          className={clsx(classes.activeRegionSelect, {
            [classes.regionSelected]: !!activePOI,
          })}
          // expects arr bc multiple mode
          value={[activePOI]}
          multiple
          disableClearable={true}
          blurOnSelect
          openOnFocus
          id="activePOI-autocomplete"
          options={options}
          onChange={handleActivePOI}
          // handles both the dropdown options and the input text
          // necessary to maintain search matching functionality
          getOptionLabel={o =>
            typeof o === 'object'
              ? o.display
              : activePOIName
          }
          getOptionSelected={(o, value) =>
            o.value === value
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label=""
              onKeyDown={e =>
                // NOTE: hack prevent typing in invisible input
                activeRegion && e.preventDefault()
              }
              InputLabelProps={{ display: 'none' }}
              hiddenLabel={true}
              placeholder={i18n.translate(
                `CONTROL_ACTIVE_REGION_DEFAULT_${activeNorm.toUpperCase()}`,
              )}
            />
          )}
        />
      </Box>
    )
  }

  return (
    <FormControl
      className={clsx(
        classes.root,
        className,
        'panel-root',
      )}
    >
      <PanelHeader
        title={i18n.translate(`DATA_CONTROLS_HEADER`)}
      />
      <PanelBody>
        <Box
          className={clsx(
            classes.controlSection,
            'control-section',
            'main',
          )}
        >
          <Box className={clsx(classes.control)}>
            {isWkkfView(activeView) && (
              <ToggleGroup
                options={['all', 'wkkf']}
                prefix="WKKF_TOGGLE_"
                selected={POIModeActive ? 'wkkf' : 'all'}
                handleChange={togglePOIMode}
                label={i18n.translate('WKKF_CONTROL_LABEL')}
              />
            )}
          </Box>
          {!!POIModeActive && getActivePOISelect()}
          <Box className={classes.control}>
            <SelectBox
              options={createOptions(
                'LABEL_',
                OPTIONS_METRIC.options,
              )}
              current={activeMetric}
              handleChange={handleActiveMetric}
              label={i18n.translate('LEGEND_SELECT_INDEX')}
            />
          </Box>
          <Box className={classes.control}>
            <ToggleGroup
              options={RATING_TYPES}
              prefix="RATING_TYPE_"
              selected={activeRatingType}
              handleChange={handleActiveRatingType}
              disableOptionCallback={op => {
                if (
                  op === 's' &&
                  activeGeoType === 'counties' &&
                  activeNorm === 's'
                ) {
                  return i18n.translate(
                    `CONTROL_NORM_COUNTY_STATE_TOOLTIP`,
                  )
                } else if (
                  op === 's' &&
                  activeGeoType === 'zips' &&
                  activeNorm === 'm'
                ) {
                  return i18n.translate(
                    `CONTROL_NORM_ZIP_METRO_TOOLTIP`,
                  )
                }
              }}
              label={i18n.translate('LEGEND_RATING_TYPE')}
              fullWidth={false}
            />
          </Box>
          <Box
            className={clsx(classes.control, 'geo-type')}
          >
            <ToggleGroup
              options={GEO_TYPES}
              prefix="GEO_TYPE_"
              selected={activeGeoType}
              handleChange={handleActiveGeoType}
              label={i18n.translate('LEGEND_GEO_TYPE')}
              disableOptionCallback={op =>
                op !== 'tracts' &&
                !!POIModeActive &&
                i18n.translate('WKKF_GEO_TOOLTIP')
              }
              fullWidth={true}
            />
          </Box>
          <Box className={classes.control}>
            <ToggleGroup
              options={OPTIONS_NORM.options}
              prefix="LEGEND_"
              selected={activeNorm}
              handleChange={handleActiveNorm}
              label={i18n.translate('LEGEND_COMPARE')}
              disableOptionCallback={op => {
                if (
                  !!POIModeActive &&
                  op === 'm' &&
                  !poi_data.sites.find(
                    s => s.id === activePOI,
                  ).metro_mode
                ) {
                  return i18n.translate(
                    'WKKF_METRO_TOOLTIP',
                  )
                } else if (
                  op === 'm' &&
                  activeGeoType === 'counties'
                ) {
                  return i18n.translate(
                    `CONTROL_NORM_COUNTY_METRO_TOOLTIP`,
                  )
                } else if (
                  op === 's' &&
                  activeGeoType === 'counties' &&
                  activeRatingType === 's'
                ) {
                  return i18n.translate(
                    `CONTROL_NORM_COUNTY_STATE_TOOLTIP`,
                  )
                } else if (
                  op === 'm' &&
                  activeGeoType === 'zips' &&
                  activeRatingType === 's'
                ) {
                  return i18n.translate(
                    `CONTROL_NORM_ZIP_METRO_TOOLTIP`,
                  )
                }
              }}
              fullWidth={true}
            />
          </Box>
          {getActiveRegionSelect()}
          <Box className={classes.control}>
            <ToggleGroup
              options={loadYears}
              prefix="LEGEND_"
              selected={activeYear}
              handleChange={handleActiveYear}
              label={i18n.translate('LEGEND_TIME')}
            />
          </Box>
          <Box
            className={clsx(
              classes.control,
              classes.switchControl,
              classes.tooltipSwitchControl,
            )}
          >
            <FormControlLabel
              control={
                <SwitchControl
                  checked={displayPopup}
                  onChange={toggleShowPopup}
                />
              }
              label={i18n.translate(`LEGEND_TOOLTIP_LABEL`)}
            />
          </Box>
        </Box>
      </PanelBody>
    </FormControl>
  )
}

export default withStyles(styles)(DataControls)
